// Burger toggle for the son-mobile-menu widget. Measures the header actions
// box so the fixed popup panel tracks the header (20px gap below it, side
// margins mirrored from the box's right edge - see _son-mobile-menu.scss),
// locks body scroll while open and keeps cart and menu mutually exclusive.
// The popup is a dialog: while open everything outside the widget is inert, so
// Tab stays in the panel, focus moves in on open and back to the burger on close.

const HEADER_GAP = 20;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

// inert every sibling along the path from <body> to the widget, so only the
// widget itself (burger + popup) stays reachable; returns what was changed
function inertOutside(widget) {
    const changed = [];
    for (let node = widget; node && node !== document.body; node = node.parentElement) {
        for (const sibling of node.parentElement.children) {
            if (sibling !== node && !sibling.inert && sibling.tagName !== 'SCRIPT') {
                sibling.inert = true;
                changed.push(sibling);
            }
        }
    }
    return changed;
}

function bindMobileMenu(widget) {
    if (widget.dataset.sonMobileMenuReady) {
        return;
    }
    widget.dataset.sonMobileMenuReady = 'true';

    const toggle = widget.querySelector('.son-mobile-menu__toggle');
    const popup = widget.querySelector('.son-mobile-menu__popup');
    const overlay = widget.querySelector('.son-mobile-menu__overlay');
    if (!toggle || !popup) {
        return;
    }

    const position = () => {
        const box = widget.closest('.son-header-actions') || widget;
        const rect = box.getBoundingClientRect();
        widget.style.setProperty('--son-mm-top', `${rect.bottom + HEADER_GAP}px`);
        widget.style.setProperty('--son-mm-inline', `${Math.max(window.innerWidth - rect.right, 0)}px`);
    };

    const isOpen = () => widget.classList.contains('is-open');
    let inerted = [];

    const setOpen = (open) => {
        if (open === isOpen()) {
            return;
        }

        // close the mini-cart first: its toggle click bubbles to the
        // outside-click handler below, which is a no-op while still closed
        if (open) {
            closeMiniCart();
            position();
        }

        widget.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        popup.setAttribute('aria-hidden', open ? 'false' : 'true');
        document.body.classList.toggle('son-scroll-lock', open);

        // not in the editor preview: Elementor re-renders the widget on every
        // setting change, which would strand the rest of the preview inert
        const editing = document.body.classList.contains('elementor-editor-active');

        if (open) {
            inerted = editing ? [] : inertOutside(widget);
            popup.querySelector(FOCUSABLE)?.focus();
        } else {
            inerted.forEach((node) => { node.inert = false; });
            inerted = [];
            if (popup.contains(document.activeElement)) {
                toggle.focus();
            }
        }
    };

    toggle.addEventListener('click', () => setOpen(!isOpen()));

    // the dimmer lives inside .son-mobile-menu, so the outside-click handler
    // below never sees it - close on it explicitly
    overlay?.addEventListener('click', () => setOpen(false));

    // covers the cart toggle too, so opening the cart closes the menu
    document.addEventListener('click', (event) => {
        if (isOpen() && !event.target.closest('.son-mobile-menu')) {
            setOpen(false);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isOpen()) {
            setOpen(false);
        }
    });

    window.addEventListener('resize', () => {
        if (!isOpen()) {
            return;
        }

        // breakpoint switch hid the widget - drop the open state with it
        if (!toggle.offsetParent) {
            setOpen(false);
            return;
        }

        position();
    });
}

function closeMiniCart() {
    // go through Elementor's own toggle so its JS state stays consistent
    document.querySelector('.elementor-menu-cart--shown .elementor-menu-cart__toggle_button')?.click();
}

function initMobileMenus(root = document) {
    root.querySelectorAll('.son-mobile-menu').forEach(bindMobileMenu);
}

document.addEventListener('DOMContentLoaded', () => initMobileMenus());

// re-bind after the Elementor editor re-renders the widget: without this the
// burger is inert in the preview, which is why the popup used to be forced
// open there instead
window.addEventListener('elementor/frontend/init', () => {
    if (window.elementorFrontend && window.elementorFrontend.hooks) {
        window.elementorFrontend.hooks.addAction('frontend/element_ready/global', ($element) => {
            initMobileMenus($element[0] || document);
        });
    }
});
